import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PartClass, PartClassSearchParams } from '../../services/partClassApi';

export interface PartClassState {
  items: PartClass[];
  selectedItem: PartClass | null;
  loading: boolean;
  error: string | null;
  formData: { Name: string; Description: string; Active: boolean };
  validationErrors: string[];
  searchParams: PartClassSearchParams;
  pagination: { page: number; pageSize: number; total: number };
  statistics: { total: number; active: number; inactive: number };
  selectedIds: number[];
  bulkOperationLoading: boolean;
  successMessage: string;
}

const initialState: PartClassState = {
  items: [], selectedItem: null, loading: false, error: null,
  formData: { Name: '', Description: '', Active: true },
  validationErrors: [],
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'Name', sortOrder: 'asc' },
  pagination: { page: 1, pageSize: 10, total: 0 },
  statistics: { total: 0, active: 0, inactive: 0 },
  selectedIds: [], bulkOperationLoading: false, successMessage: '',
};

const partClassSlice = createSlice({
  name: 'partClass', initialState,
  reducers: {
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; },
    setItems: (s, a: PayloadAction<PartClass[]>) => { s.items = a.payload; },
    setSelectedItem: (s, a: PayloadAction<PartClass | null>) => { s.selectedItem = a.payload; },
    addItem: (s, a: PayloadAction<PartClass>) => { s.items.unshift(a.payload); },
    updateItem: (s, a: PayloadAction<PartClass>) => {
      const i = s.items.findIndex(x => x.Id === a.payload.Id);
      if (i !== -1) s.items[i] = a.payload;
      if (s.selectedItem?.Id === a.payload.Id) s.selectedItem = a.payload;
    },
    removeItem: (s, a: PayloadAction<number>) => {
      s.items = s.items.filter(x => x.Id !== a.payload);
      if (s.selectedItem?.Id === a.payload) s.selectedItem = null;
    },
    setFormData: (s, a: PayloadAction<Partial<PartClassState['formData']>>) => { s.formData = { ...s.formData, ...a.payload }; },
    resetFormData: (s) => { s.formData = initialState.formData; },
    setValidationErrors: (s, a: PayloadAction<string[]>) => { s.validationErrors = a.payload; },
    clearValidationErrors: (s) => { s.validationErrors = []; },
    setSearchParams: (s, a: PayloadAction<Partial<PartClassSearchParams>>) => { s.searchParams = { ...s.searchParams, ...a.payload } as any; },
    resetSearchParams: (s) => { s.searchParams = initialState.searchParams; },
    setPagination: (s, a: PayloadAction<Partial<PartClassState['pagination']>>) => { s.pagination = { ...s.pagination, ...a.payload } as any; },
    setStatistics: (s, a: PayloadAction<PartClassState['statistics']>) => { s.statistics = a.payload; },
    setSelectedIds: (s, a: PayloadAction<number[]>) => { s.selectedIds = a.payload; },
    addSelectedId: (s, a: PayloadAction<number>) => { if (!s.selectedIds.includes(a.payload)) s.selectedIds.push(a.payload); },
    removeSelectedId: (s, a: PayloadAction<number>) => { s.selectedIds = s.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (s) => { s.selectedIds = []; },
    setBulkOperationLoading: (s, a: PayloadAction<boolean>) => { s.bulkOperationLoading = a.payload; },
    setSuccessMessage: (s, a: PayloadAction<string>) => { s.successMessage = a.payload; },
    clearSuccessMessage: (s) => { s.successMessage = ''; },
    resetPartClassState: () => initialState,
  }
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setFormData, resetFormData, setValidationErrors, clearValidationErrors,
  setSearchParams, resetSearchParams, setPagination, setStatistics,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds, setBulkOperationLoading,
  setSuccessMessage, clearSuccessMessage, resetPartClassState,
} = partClassSlice.actions;

export const FETCH_PART_CLASS = 'partClass/fetch';
export const FETCH_PART_CLASS_BY_ID = 'partClass/fetchById';
export const CREATE_PART_CLASS = 'partClass/create';
export const UPDATE_PART_CLASS = 'partClass/update';
export const DELETE_PART_CLASS = 'partClass/delete';
export const SEARCH_PART_CLASS = 'partClass/search';
export const FETCH_PART_CLASS_STATS = 'partClass/fetchStats';
export const BULK_PART_CLASS_ACTIVATE = 'partClass/bulkActivate';
export const BULK_PART_CLASS_DEACTIVATE = 'partClass/bulkDeactivate';
export const BULK_PART_CLASS_DELETE = 'partClass/bulkDelete';
export const EXPORT_PART_CLASS_CSV = 'partClass/exportCsv';

export const fetchPartClass = (p?: PartClassSearchParams) => ({ type: FETCH_PART_CLASS, payload: p });
export const fetchPartClassById = (id: number) => ({ type: FETCH_PART_CLASS_BY_ID, payload: id });
export const createPartClass = (item: Omit<PartClass, 'Id'>) => ({ type: CREATE_PART_CLASS, payload: item });
export const updatePartClass = (id: number, item: Partial<PartClass>) => ({ type: UPDATE_PART_CLASS, payload: { id, item } });
export const deletePartClass = (id: number) => ({ type: DELETE_PART_CLASS, payload: id });
export const searchPartClass = (p: PartClassSearchParams) => ({ type: SEARCH_PART_CLASS, payload: p });
export const fetchPartClassStats = () => ({ type: FETCH_PART_CLASS_STATS });
export const bulkPartClassActivate = (ids: number[]) => ({ type: BULK_PART_CLASS_ACTIVATE, payload: ids });
export const bulkPartClassDeactivate = (ids: number[]) => ({ type: BULK_PART_CLASS_DEACTIVATE, payload: ids });
export const bulkPartClassDelete = (ids: number[]) => ({ type: BULK_PART_CLASS_DELETE, payload: ids });
export const exportPartClassCsv = (p?: PartClassSearchParams) => ({ type: EXPORT_PART_CLASS_CSV, payload: p });

export default partClassSlice.reducer; 